import * as React from "react";

import * as R from "ramda";

import classNames from "classnames";

import { newSetKeyValueChange } from "../../../helpers/Doc/changes";

import ReasoningToolPanel from "./Panel";
import Comments from "./AssessmentQuestionDetails/Comments";
import SelectField from './AssessmentQuestionDetails/SelectField';
import GenLoadingSpinner from '../../Gen/LoadingSpinner/component'

import { getAdminAssessmentQuestion, updateBulkAdminAssessmentQuestions } from "../../../api/sdk.gen";
import MySwal from "../../../ui/MySwal";
import { formatLabel } from "../../../helpers/utils";

interface ReasoningToolAssessmentQuestionDataPanelProps {
  editingContext: EditingContextInEditor;
  editingContextExtra: EditingContextExtra;
  userContext: UserContext;
  cci_status: string;
};

type AssessmentQuestionMetadata = Partial<Pick<
    AssessmentQuestion,
    'clinical_location' |
    'final_diagnosis' |
    'patient_age' |
    'system' |
    'clinical_focus' |
    'current_use' |
    'clinical_disciplines' |
    'clinical_excellence' |
    'basic_science_disciplines' |
    'sciences_core_concepts'
  >>;

export default function ReasoningToolAssessmentQuestionDataPanel(props: ReasoningToolAssessmentQuestionDataPanelProps) {
  const {
    editingContext,
    editingContextExtra,
    userContext,
    cci_status,
  } = props;

  const {
    assessmentQuestionId,
    assessmentQuestionStatistics,
    assessmentQuestionEditingData,
  } = editingContextExtra;

  const [label, setLabel] = React.useState<string>()
  const [assessmentQuestion, setAssessmentQuestion] = React.useState<AssessmentQuestion>()
  const [assessmentQuestionMetaData, setAssessmentQuestionMetadata] = React.useState<AssessmentQuestionMetadata>({})
  const [validityStatus, setValidityStatus] = React.useState<string>()

  React.useEffect(() => {
    getAdminAssessmentQuestion({ path: { id: assessmentQuestionId.toString() } })
      .then(response => {
        const question = response.data as unknown as AssessmentQuestion;
        setAssessmentQuestion(question);
        setValidityStatus(question.status);
      })
  }, [assessmentQuestionId]);

  const saveMetadata = React.useCallback(async (event) => {
    event.preventDefault();

    const result = await updateBulkAdminAssessmentQuestions({
      body: { data: [{
        id: assessmentQuestionId,
        ...assessmentQuestionMetaData,
        clinical_disciplines: assessmentQuestionMetaData.clinical_disciplines?.join(", "),
        clinical_excellence: assessmentQuestionMetaData.clinical_excellence?.join(", ")
      }] },
    });

    if (result.error || result.data.error > 0) {
      MySwal.fire("Error!", `Failed to save changes. ${(result as { error: { error: string } }).error.error}`, "error");
    } else {
      MySwal.fire("Saved!", "Changes have been saved.", "success");
    }
  }, [assessmentQuestionId, assessmentQuestionMetaData])

  const saveValidityStatus = React.useCallback(async (status: string) => {
    const previousStatus = validityStatus;
    setValidityStatus(status);

    const result = await updateBulkAdminAssessmentQuestions({
      body: { data: [{ id: assessmentQuestionId, status }] },
    });

    if (result.error || (result.data && result.data.error > 0)) {
      setValidityStatus(previousStatus);
      MySwal.fire(
        "Error!",
        `Failed to save changes. ${(result as { error: { error: string } }).error?.error || (result as { data?: { error_msgs?: string } }).data?.error_msgs || ""}`,
        "error"
      );
    } else {
      setAssessmentQuestion((question) => question ? { ...question, status } : question);
      MySwal.fire("Saved!", "Changes have been saved.", "success");
    }
  }, [assessmentQuestionId, validityStatus])

  if (!assessmentQuestion) {
    return (
      <ReasoningToolPanel
        key="ASSESSMENT QUESTION DATA"
        title="ASSESSMENT QUESTION DATA"
      >
        <GenLoadingSpinner />
      </ReasoningToolPanel>
    );
  }

  const allowedEditQuestionMetadata = userContext.permissions.allowedEditMode === "Editing";
  const readonly = !userContext.isAdmin && !allowedEditQuestionMetadata;
  const canEditValidityStatus = userContext.isAdmin;
  const validityStatusOptions = editingContextExtra.assessmentQuestionStatusOptions || [];
  const displayedValidityStatus = validityStatus
    ? formatLabel(validityStatus)
    : editingContextExtra.assessmentStatus;

  const selectedAssessmentQuestionStatistic = assessmentQuestionStatistics.find((stat) => stat.label === label) || assessmentQuestionStatistics[0];

  return (
    <ReasoningToolPanel
      key="ASSESSMENT QUESTION DATA"
      title="ASSESSMENT QUESTION DATA"
    >
      <h2 style={{ padding: 10 }}>Assessment Question Meta Data</h2>

      <div>
        <SelectField
          readonly={readonly}
          label="Question Use"
          value={assessmentQuestionMetaData.current_use || assessmentQuestion.current_use}
          options={editingContextExtra.assessmentQuestionQuestionUseOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, current_use: value } : R.omit(['current_use'], oldMetadata)) }}
        />
        <SelectField
          readonly={readonly}
          label="Clinical Location"
          value={assessmentQuestionMetaData.clinical_location || assessmentQuestion.clinical_location}
          options={editingContextExtra.assessmentQuestionClinicalLocationOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, clinical_location: value } : R.omit(['clinical_location'], oldMetadata)) }}
        />
        <SelectField
          readonly={readonly}
          label="Final Diagnosis"
          value={assessmentQuestionMetaData.final_diagnosis || assessmentQuestion.final_diagnosis}
          options={editingContextExtra.assessmentQuestionFinalDiagnosisOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, final_diagnosis: value } : R.omit(['final_diagnosis'], oldMetadata)) }}
        />
        <SelectField
          readonly={readonly}
          label="Patient Age"
          value={assessmentQuestionMetaData.patient_age || assessmentQuestion.patient_age}
          options={editingContextExtra.assessmentQuestionPatientAgeOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, patient_age: value } : R.omit(['patient_age'], oldMetadata)) }}
        />
        <SelectField
          readonly={readonly}
          label="System"
          value={assessmentQuestionMetaData.system || assessmentQuestion.system}
          options={editingContextExtra.assessmentQuestionSystemOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, system: value } : R.omit(['system'], oldMetadata)) }}
        />
        <SelectField
          readonly={readonly}
          label="Clinical Focus"
          value={assessmentQuestionMetaData.clinical_focus || assessmentQuestion.clinical_focus}
          options={editingContextExtra.assessmentQuestionClinicalFocusOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, clinical_focus: value } : R.omit(['clinical_focus'], oldMetadata)) }}
        />
        <SelectField
          readonly={readonly}
          label="Clinical Discipline"
          value={assessmentQuestionMetaData.clinical_disciplines?.[0] || assessmentQuestion.clinical_disciplines?.[0] || null}
          options={editingContextExtra.assessmentQuestionClinicalDisciplineOptions}
          onChange={(value: string | null) => {
            setAssessmentQuestionMetadata(oldMetadata =>
              value
                ? { ...oldMetadata, clinical_disciplines: [value] }
                : R.omit(['clinical_disciplines'], oldMetadata)
            );
          }}
        />
        <SelectField
          readonly={readonly}
          label="Clinical Excellence"
          value={assessmentQuestionMetaData.clinical_excellence?.[0] || assessmentQuestion.clinical_excellence?.[0] || null}
          options={editingContextExtra.assessmentQuestionClinicalExcellenceOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, clinical_excellence: [value] } : R.omit(['clinical_excellence'], oldMetadata)) }}
        />
        <SelectField
          readonly={readonly}
          label="Basic Science Disciplines"
          value={assessmentQuestionMetaData.basic_science_disciplines || assessmentQuestion.basic_science_disciplines || null}
          options={editingContextExtra.assessmentQuestionBasicScienceDisciplinesOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, basic_science_disciplines: value } : R.omit(['basic_science_disciplines'], oldMetadata)) }}
        />
        <SelectField
          readonly={readonly}
          label="Sciences Core Concepts"
          value={assessmentQuestionMetaData.sciences_core_concepts || assessmentQuestion.sciences_core_concepts || null}
          options={editingContextExtra.assessmentQuestionSciencesCoreConceptsOptions}
          onChange={(value: string | null) => { setAssessmentQuestionMetadata((oldMetadata) => value ? { ...oldMetadata, sciences_core_concepts: value } : R.omit(['sciences_core_concepts'], oldMetadata)) }}
        />

        {!readonly &&
          <div className="reasoning-tool-stat">
            <span className="reasoning-tool-stat-label">
              <button className='aq-button' onClick={saveMetadata} >Save</button>
            </span>
          </div> }
      </div>

      <Comments questionId={assessmentQuestion.id} userContext={userContext} />

      <h2 style={{padding: 10}}>Assessment Question Data And Statuses</h2>

      <div>
        <div className="reasoning-tool-stat">
          <span className="reasoning-tool-stat-label">Item Decisions</span>
          { readonly ? (
              <span className="reasoning-tool-stat-value">{editingContextExtra.cci_status || '-'}</span>
            ) : (
              <div className={classNames("reasoning-tool-stat-value reasoning-tool-item-decision-input", {changed: cci_status && cci_status !== assessmentQuestion.cci_status})}>
                <select value={editingContextExtra.cci_status} onChange={(e) => {
                  editingContext.addChange(newSetKeyValueChange(editingContextExtra.rootUid, "cci_status", e.target.value));
                }}>
                  {editingContextExtra.cciStatusOptions.map((cciStatus) => <option key={cciStatus.key} value={cciStatus.key}>{cciStatus.displayName}</option>)}
                </select>
              </div>
            )}
        </div>

        <div className="reasoning-tool-stat">
          <span className="reasoning-tool-stat-label">Academic Year for this Data:</span>
          { readonly ? (
              <span className="reasoning-tool-stat-value">{selectedAssessmentQuestionStatistic?.label || '-'}</span>
            ) : (
              <span className="reasoning-tool-stat-value reasoning-tool-item-decision-input">
                <select value={selectedAssessmentQuestionStatistic?.label} onChange={(e) => setLabel(e.target.value)}>
                  {assessmentQuestionStatistics.map((statistic) => <option key={statistic.label} value={statistic.label}>{statistic.label}</option>)}
                </select>
              </span>
            )}
        </div>

        <div className="reasoning-tool-stat">
          <span className="reasoning-tool-stat-label"># Administrations:</span>
          <span className="reasoning-tool-stat-value">{selectedAssessmentQuestionStatistic?.administrations_count}</span>
        </div>

        <div className="reasoning-tool-stat">
          <span className="reasoning-tool-stat-label">Difficulty (pdiff):</span>
          <span className="reasoning-tool-stat-value">{selectedAssessmentQuestionStatistic?.difficulty}</span>
        </div>

        <div className="reasoning-tool-stat">
          <span className="reasoning-tool-stat-label">Discrimination (rpb):</span>
          <span className="reasoning-tool-stat-value">{selectedAssessmentQuestionStatistic?.discrimination}</span>
        </div>

        <div className="reasoning-tool-stat">
          <span className="reasoning-tool-stat-label">Item Validity Status:</span>
          {canEditValidityStatus ? (
            <span className="reasoning-tool-stat-value reasoning-tool-item-decision-input">
              <select
                value={validityStatus || ""}
                onChange={(e) => saveValidityStatus(e.target.value)}
              >
                {validityStatusOptions.map((status) => (
                  <option key={status} value={status}>{formatLabel(status)}</option>
                ))}
              </select>
            </span>
          ) : (
            <span className="reasoning-tool-stat-value">{displayedValidityStatus}</span>
          )}
        </div>

        <div className="reasoning-tool-stat">
          <span className="reasoning-tool-stat-label">Used on Assessment?</span>
          <span className="reasoning-tool-stat-value">{assessmentQuestionEditingData && (assessmentQuestionEditingData?.delivered ? "Yes" : "No")}</span>
        </div>
      </div>

      <hr />

      <div>
        <h2 style={{padding: 10}}>Answer Option Admin Data</h2>

        {[...Array(5).keys()].map((index) => {
          const capitalLetter = String.fromCharCode("A".charCodeAt(0) + index);
          const lowerLetter = String.fromCharCode("a".charCodeAt(0) + index);
          const correct = index === assessmentQuestion?.content?.correct_answer_index;

          return (
            <div key={index} className={classNames("reasoning-tool-stat", {correct: correct})}>
              <span className="reasoning-tool-stat-label">Admin {capitalLetter}:</span>
              <span className="reasoning-tool-stat-value">{selectedAssessmentQuestionStatistic && selectedAssessmentQuestionStatistic[`choice_${lowerLetter}_count`]}</span>
              {correct && <span className="reasoning-tool-stat-comment">Correct!</span>}
            </div>
          );
        })}

        <div className="reasoning-tool-stat">
          <span className="reasoning-tool-stat-label">Admin Omit:</span>
          <span className="reasoning-tool-stat-value">{selectedAssessmentQuestionStatistic?.skips_count}</span>
        </div>
      </div>
      <hr />
    </ReasoningToolPanel>
  );
}
